import { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMe } from 'com/App/WithMe';
import { useHistoryModal } from 'com/HistoryModal';
import { apiMoveWordToDeck } from 'core/api';
import { getEntries } from 'core/helpers/object';
import { prettifyText } from 'core/helpers/text';
import { getDeckExerciseUrl } from 'core/paths';
import { MenuItem } from 'core/ui/Menu';
import { useMessageModal } from 'core/ui/MessageModal';
import ICONS from 'core/ui/icons';

type TProps = {
  deck: TDeck;
  word: TWord | TWordUpdate;
  onRemoveWord?: (wordUid: string) => Promise<void> | void;
};

export default memo(function MenuBody({ deck, word, onRemoveWord }: TProps) {
  const me = useMe();
  const { showError } = useMessageModal();
  const navigate = useNavigate();
  const { showWordHistory } = useHistoryModal();

  const removeWord = async () => {
    try {
      if (!('uid' in word) || !onRemoveWord) {
        throw new Error('TWordUpdate is not supported');
      }

      if (!confirm('Are you sure?')) return;

      await onRemoveWord(word.uid);
    } catch (error) {
      showError(error);
    }
  };

  return (
    <>
      <CopyMenuItem deck={deck} word={word} />
      <MenuItem label="Remove" icon={ICONS.Trash} onClick={removeWord} />
      {'uid' in word && me?.isAdmin && (
        <>
          <MenuItem
            label="Move to another deck"
            icon={ICONS.ArrowRight}
            onClick={() => {
              // TODO: make a dialog.
              const deckUid = prompt('Provide deckUid');
              if (!deckUid) return;
              apiMoveWordToDeck(word.uid, deckUid).catch(showError);
            }}
          />
          <MenuItem
            label="Play card"
            icon={ICONS.Play}
            onClick={() =>
              navigate(
                getDeckExerciseUrl(deck.uid, TExercise.wordCards, word.uid)
              )
            }
          />
        </>
      )}
      {'uid' in word && (
        <MenuItem
          label="Show status"
          icon={ICONS.Graph}
          onClick={() => showWordHistory(word.uid)}
        />
      )}
    </>
  );
});

function CopyMenuItem({
  deck,
  word,
}: {
  deck: TDeck;
  word: TWord | TWordUpdate;
}) {
  const { showError, showMessage } = useMessageModal();
  const textToCopy = useMemo(() => {
    const definitions = getEntries<PropertyKey, string | undefined>(
      word.definitions
    )
      .map(([lang, text]) => `${lang ? `${String(lang)}:` : ''} ${text}`)
      .join('\n');
    const deckLang = deck.lang ? `${deck.lang}: ` : '';

    return prettifyText(
      `
        ${definitions}${word.note ? `\nNote: ${word.note}` : ''}
        ${deckLang}${word.value}${
          word.valueNote ? `\nValue note: ${word.valueNote}` : ''
        }
      `
        .replace(/^[ \t]+/gm, '')
        .trim()
    );
  }, [word]);

  return (
    <MenuItem
      label="Copy"
      icon={ICONS.Cards}
      onClick={() => {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => showMessage('Copied!'))
          .catch(showError);
      }}
    />
  );
}
